import React from "react";
import { translateRuntime } from "../../i18n.jsx";

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "border-zinc-300 bg-zinc-100 text-zinc-700",
    dark: "border-zinc-900 bg-zinc-900 text-white",
    green: "border-emerald-300 bg-emerald-50 text-emerald-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    red: "border-rose-300 bg-rose-50 text-rose-800",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    violet: "border-violet-300 bg-violet-50 text-violet-800",
    rose: "border-rose-300 bg-rose-50 text-rose-800",
  };
  return <span className={cx("inline-flex border px-2 py-0.5 text-xs font-black", tones[tone] || tones.neutral)}>{children}</span>;
}

export function Frame({ title, right, children, className = "" }) {
  return (
    <section className={cx("border border-zinc-300 bg-white shadow-sm", className)}>
      <div className="flex min-h-10 items-center justify-between border-b border-zinc-200 bg-zinc-50 px-4 py-2">
        <h3 className="text-xs font-black uppercase tracking-[0.14em] text-zinc-600">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Toast({ toast, clear }) {
  if (!toast?.message) return null;
  return (
    <div className={cx("fixed bottom-4 right-4 z-50 max-w-md border p-4 shadow-xl", toast.kind === "error" ? "border-rose-300 bg-rose-50 text-rose-900" : "border-emerald-300 bg-emerald-50 text-emerald-900")}>
      <div className="flex items-start justify-between gap-4">
        <div className="text-sm">{toast.message}</div>
        <button onClick={clear} className="text-xs opacity-70 hover:opacity-100">schließen</button>
      </div>
    </div>
  );
}

export function genreStyle(genre) {
  const g = String(genre || "").toLowerCase();
  if (g.includes("techno") || g.includes("rave") || g.includes("hard")) {
    return { tone: "rose", badge: "border-rose-300 bg-rose-50 text-rose-900", event: "border-l-4 border-rose-500 bg-rose-50 text-rose-950" };
  }
  if (g.includes("afro") || g.includes("house")) {
    return { tone: "blue", badge: "border-blue-300 bg-blue-50 text-blue-900", event: "border-l-4 border-blue-500 bg-blue-50 text-blue-950" };
  }
  if (g.includes("latin") || g.includes("urban") || g.includes("reggaeton")) {
    return { tone: "amber", badge: "border-amber-300 bg-amber-50 text-amber-900", event: "border-l-4 border-amber-500 bg-amber-50 text-amber-950" };
  }
  if (g.includes("hip") || g.includes("rap") || g.includes("trap")) {
    return { tone: "violet", badge: "border-violet-300 bg-violet-50 text-violet-900", event: "border-l-4 border-violet-500 bg-violet-50 text-violet-950" };
  }
  if (g.includes("pop") || g.includes("charts")) {
    return { tone: "green", badge: "border-emerald-300 bg-emerald-50 text-emerald-900", event: "border-l-4 border-emerald-500 bg-emerald-50 text-emerald-950" };
  }
  return { tone: "neutral", badge: "border-zinc-300 bg-zinc-50 text-zinc-800", event: "border-l-4 border-zinc-400 bg-zinc-50 text-zinc-950" };
}

export function soundcheckStatus(event, requests = []) {
  // RC8_FIX3:
  // Wichtig: "not_planned" enthält das Wort "planned".
  // Deshalb muss not_planned VOR planned/confirmed geprüft werden.
  const explicit = String(event?.soundcheck_status || "").toLowerCase().trim();

  if (!explicit || explicit === "not_planned" || explicit === "open" || explicit === "none" || explicit === "undefined") {
    return "open";
  }

  const eventRequests = requests.filter((r) => r.request_type === "soundcheck" || String(r.title || "").toLowerCase().includes("soundcheck"));
  const activeRequests = eventRequests.filter((r) => !["archived", "closed", "done", "read", "replied"].some((x) => String(r.status || "").toLowerCase().includes(x)));

  const hasConfirmed = activeRequests.some((r) => {
    const s = String(r.status || "").toLowerCase().trim();
    return s === "confirmed" || s === "planned" || s === "discussed";
  });
  const hasRequested = activeRequests.some((r) => {
    const s = String(r.status || "").toLowerCase().trim();
    return s === "requested" || s === "organizer_requested";
  });
  const hasCounter = activeRequests.some((r) => {
    const s = String(r.status || "").toLowerCase().trim();
    return s === "counter" || s === "counter_proposal";
  });

  if (explicit === "confirmed" || explicit === "planned" || explicit === "discussed" || explicit === "bestätigt" || explicit === "besprochen" || hasConfirmed) {
    return "confirmed";
  }
  if (explicit === "counter" || explicit === "counter_proposal" || hasCounter) {
    return "counter";
  }
  if (explicit === "requested" || explicit === "organizer_requested" || explicit === "angefragt" || hasRequested) {
    return "requested";
  }
  if (explicit === "declined" || explicit === "cancelled") {
    return "declined";
  }

  return "open";
}

export function soundcheckToneFromStatus(status) {
  if (status === "confirmed") return "green";
  if (status === "requested" || status === "counter") return "amber";
  if (status === "declined") return "red";
  return "neutral";
}

export function soundcheckLabelFromStatus(status) {
  const lang = localStorage.getItem("lenox_language") || "de";
  if (status === "confirmed") return t("confirmed", lang);
  if (status === "requested") return t("requested", lang);
  if (status === "counter") return lang === "fr" ? "contre-proposition" : lang === "pt" ? "contraproposta" : lang === "en" ? "counter proposal" : "Gegenvorschlag";
  if (status === "declined") return lang === "fr" ? "impossible" : lang === "pt" ? "não possível" : lang === "en" ? "not possible" : "nicht möglich";
  return t("open", lang);
}

export function formatDisplayDate(dateString) {
  if (!dateString) return "Datum offen";
  const d = new Date(`${dateString}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

export function isoDate(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function eventDate(event) {
  return (event?.start_at || "").slice(0, 10) || isoDate(new Date());
}

export function eventLine(event) {
  return `${event?.start_at || "Start offen"} → ${event?.end_at || "Ende offen"}`;
}

export function addMinutes(date, time, minutes) {
  const [h, m] = String(time || "18:00").split(":").map(Number);
  const d = new Date(`${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
  d.setMinutes(d.getMinutes() + Number(minutes || 60));
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function inboxItemClosed(item) {
  // RC8_FIX4:
  // "read/gelesen" is NOT the same as erledigt.
  // A message with needs_reply=true must stay in Offen, even after opening.
  const status = String(item?.status || "").toLowerCase().trim();
  if (["archived", "closed", "done", "replied", "answered", "resolved"].some((x) => status.includes(x))) {
    return true;
  }

  // Pure info messages without required reply can become closed once read.
  if ((status === "read" || status === "seen") && item?.needs_reply === false) {
    return true;
  }

  return false;
}

export function inboxItemHot(item) {
  if (!item || inboxItemClosed(item)) return false;
  const status = String(item.status || "").toLowerCase().trim();

  // Needs action stays hot/open, even when read.
  if (item.needs_reply) return true;

  // Unread info messages blink until opened.
  if (status === "unread" || status === "new") return true;

  return false;
}

export function inboxItemOpen(item) {
  return !inboxItemClosed(item);
}

export function inboxItemStateLabel(item, lang = "de") {
  if (inboxItemClosed(item)) {
    return lang === "fr" ? "terminé" : lang === "pt" ? "concluído" : lang === "en" ? "done" : "erledigt";
  }
  const status = String(item?.status || "").toLowerCase().trim();
  if (status === "read" || status === "seen") {
    return lang === "fr" ? "lu / ouvert" : lang === "pt" ? "lido / aberto" : lang === "en" ? "read / open" : "gelesen / offen";
  }
  return lang === "fr" ? "ouvert" : lang === "pt" ? "aberto" : lang === "en" ? "open" : "offen";
}

export function roleFor(user) {
  // Rollen kommen ausschließlich aus dem authentifizierten Backend-Account.
  // Namen/Benutzernamen dürfen niemals eine Rolle simulieren.
  const role = String(user?.role_id || "").toLowerCase();
  if (role === "root") return "root";
  if (role === "tech") return "tech";
  if (role === "club_owner") return "chef";
  if (role === "organizer") return "va";
  if (role === "dj") return "dj";
  return "guest";
}


export const LANGUAGES = {
  de: { code: "de", label: "Deutsch", flag: "🇩🇪" },
  en: { code: "en", label: "English", flag: "🇬🇧" },
  fr: { code: "fr", label: "Français", flag: "🇫🇷" },
  pt: { code: "pt", label: "Português", flag: "🇵🇹" },
};

export function langInfo(code) {
  return LANGUAGES[code] || LANGUAGES.de;
}

export function userLanguage(user) {
  return user?.language || localStorage.getItem("lenox_language") || "de";
}

export function t(key, lang = "de", fallback = "", variables = {}) {
  const aliases = {
    dashboard: "nav.dashboard",
    upcoming: "nav.upcoming",
    calendar: "nav.calendar",
    messages: "nav.communication",
    inbox: "nav.inbox",
    portals: "nav.portals",
    work_items: "nav.work_items",
    create_event: "nav.create_event",
    logout: "common.logout",
    soundcheck: "event.soundcheck",
    open: "common.open",
    not_planned: "work.state_planned",
    requested: "work.request",
    confirmed: "work.status_approved",
  };
  return translateRuntime(aliases[key] || key, lang, fallback || key, variables);
}

export function filterEventsForUser(events) {
  // The API is the security and assignment authority. Organizer/DJ event
  // lists are already scoped server-side, so the UI must not hide valid
  // events again through fragile username/name heuristics.
  return Array.isArray(events) ? events : [];
}

export function translationPreview(text, fromLang, toLang) {
  if (!text) return "";
  if (!fromLang || !toLang || fromLang === toLang) return text;
  return `[${langInfo(fromLang).flag} → ${langInfo(toLang).flag}] ${text}`;
}


export function translationEngineLabel(engine) {
  if (!engine) return "hybrid";
  const value = String(engine).toLowerCase();
  if (value.includes("argos")) return "Argos lokal";
  if (value.includes("ollama")) return "Ollama lokal";
  if (value.includes("libre")) return "LibreTranslate";
  if (value.includes("fallback")) return "Fallback (keine echte MT)";
  if (value.includes("same-language")) return "Originalsprache";
  if (value.includes("hybrid")) return "Hybrid";
  return engine;
}
